import {useEffect, useCallback} from 'react';
import useForceUpdate from '../utils/react/useForceUpdate';
import {isEqual, once} from 'lodash';
import fetchJSON from '../utils/fetchJSON';

function createUseGlobalState<T>(initial: T) {
    let state: T = initial;
    const forceUpdates: Set<() => void> = new Set();

    function setGlobalState(nextState: T) {
        if (isEqual(nextState, state)) return;
        state = nextState;
        forceUpdates.forEach(forceUpdate => forceUpdate());
    }
    return function useGlobalState(): [T, (nextState: T) => void] {
        const forceUpdate = useForceUpdate();
        useEffect(() => {
            forceUpdates.add(forceUpdate);
            return () => { forceUpdates.delete(forceUpdate); };
        }, [forceUpdate]);
        return [state, setGlobalState];
    };
}

const useGlobalState = createUseGlobalState<User | undefined>(undefined);

const initialUser = once(async (setUser: (user: User) => void) => {
    setUser((await fetchJSON('/profile/api/')));
});

export default function (): [User | undefined, () => void] {
    const [user, setUser] = useGlobalState();

    const updateUser = useCallback(async () => {
        setUser((await fetchJSON('/profile/api/')));
    }, [setUser]);

    useEffect(() => { initialUser(setUser); }, [setUser]);

    return [user, updateUser];
}
